import { useEffect, useState } from "react";
import { Ticket, AlertCircle, RefreshCw, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { authenticatedFetch } from "@/lib/auth";
import { TicketFilterBar, FilterParams } from "@/components/TicketFilterBar";
import { TicketListAdmin } from "@/components/TicketListAdmin";
import { TicketActionModal } from "@/components/TicketActionModal";
import { AssignTicketModal } from "@/components/AssignTicketModal";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";

interface TicketData {
    id: number;
    ticket_number: string;
    subject: string;
    status: string;
    priority: string;
    client_id: number;
    client_name: string;
    created_by: number;
    created_by_name: string;
    assigned_to: number | null;
    assigned_to_name: string | null;
    created_at: string;
    updated_at: string;
    first_response_at: string | null;
    messages_count: number;
    attachments_count: number;
    time_spent: number;
    days_open: number;
}

interface PaginationData {
    page: number;
    per_page: number;
    total: number;
    total_pages: number;
}

export const AdminTicketsPage = () => {
    const { toast } = useToast();
    const navigate = useNavigate();
    const [tickets, setTickets] = useState<TicketData[]>([]);
    const [pagination, setPagination] = useState<PaginationData>({
        page: 1,
        per_page: 20,
        total: 0,
        total_pages: 0,
    });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentFilters, setCurrentFilters] = useState<FilterParams>({});
    const [currentUser, setCurrentUser] = useState<any>(null);

    // Modal
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null);
    const [selectedTicketNumber, setSelectedTicketNumber] = useState("");
    const [selectedTicketStatus, setSelectedTicketStatus] = useState("");

    // Modal de atribuição
    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [assignTicketId, setAssignTicketId] = useState<number | null>(null);
    const [assignTicketNumber, setAssignTicketNumber] = useState("");
    const [assignMode, setAssignMode] = useState<'assign' | 'transfer'>('assign');
    const [assignTicketData, setAssignTicketData] = useState<TicketData | null>(null);

    // Carregar tickets
    const fetchTickets = async (filters: FilterParams) => {
        setIsLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams();

            if (filters.client_id) params.append("client_id", filters.client_id.toString());
            if (filters.status?.length) {
                filters.status.forEach(s => params.append("status[]", s));
            }
            if (filters.priority?.length) {
                filters.priority.forEach(p => params.append("priority[]", p));
            }
            if (filters.only_mine) params.append("only_mine", "true");
            if (filters.search) params.append("search", filters.search);
            if (filters.created_from) params.append("created_from", filters.created_from);
            if (filters.created_to) params.append("created_to", filters.created_to);

            params.append("page", (filters.page || 1).toString());
            params.append("per_page", (filters.per_page || 20).toString());
            params.append("sort", filters.sort || "created_at");
            params.append("order", filters.order || "DESC");

            const response = await authenticatedFetch(
                `/api/tickets/index.php?${params.toString()}`
            );

            if (!response.ok) {
                throw new Error("Erro ao carregar tickets");
            }

            const result = await response.json();
            
            console.log('[AdminTickets] Response:', result);

            // O endpoint retorna { data: tickets[], pagination: { page, limit, total, pages } }
            if (result.data) {
                setTickets(result.data || []);
                
                // Mapear campos da paginação
                const paginationData = result.pagination || {};
                setPagination({
                    page: paginationData.page || 1,
                    per_page: paginationData.limit || 20,
                    total: paginationData.total || 0,
                    total_pages: paginationData.pages || 0,
                });
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : "Erro ao carregar tickets");
        } finally {
            setIsLoading(false);
        }
    };

    // Efeito inicial
    useEffect(() => {
        // Carregar dados do usuário logado
        const userData = localStorage.getItem('user_data');
        if (userData) {
            const user = JSON.parse(userData);
            // Garantir que o ID seja número
            if (user.id) {
                user.id = typeof user.id === 'string' ? parseInt(user.id, 10) : user.id;
            }
            setCurrentUser(user);
        }
        
        fetchTickets({});
    }, []);

    // Handle filtro
    const handleFilter = (filters: FilterParams) => {
        setCurrentFilters(filters);
        fetchTickets(filters);
    };

    // Handle ação
    const handleAction = (action: string, ticketId: number) => {
        console.log("handleAction chamado:", action, ticketId);
        const ticket = tickets.find(t => t.id === ticketId);
        if (!ticket) return;

        if (action === "view") {
            // Navegar para detalhes do ticket
            window.location.href = `/cliente/tickets/${ticketId}`;
        } else if (action === "assign") {
            // Abrir modal de atribuição
            console.log("Abrindo modal de atribuição...");
            const ticket = tickets.find(t => t.id === ticketId);
            if (!ticket) return;
            
            setAssignTicketId(ticketId);
            setAssignTicketNumber(ticket.ticket_number);
            setAssignTicketData(ticket);
            setAssignMode('assign');
            setAssignModalOpen(true);
            console.log("Estados atualizados:", { assignTicketId: ticketId, assignModalOpen: true });
        } else if (action === "transfer") {
            // Abrir modal de transferência
            console.log("Abrindo modal de transferência...");
            const ticket = tickets.find(t => t.id === ticketId);
            if (!ticket) return;
            
            setAssignTicketId(ticketId);
            setAssignTicketNumber(ticket.ticket_number);
            setAssignTicketData(ticket);
            setAssignMode('transfer');
            setAssignModalOpen(true);
            console.log("Estados atualizados:", { assignTicketId: ticketId, mode: 'transfer', assignModalOpen: true });
        } else if (action === "start") {
            // Iniciar trabalho (mudar para in_progress)
            handleStartWork(ticketId);
        } else {
            // Abrir modal para respond, ask_info, close
            setSelectedTicketId(ticketId);
            setSelectedTicketNumber(ticket.ticket_number);
            setSelectedTicketStatus(ticket.status);
            setModalOpen(true);
        }
    };

    // Atribuir ticket para mim
    const handleAssignToMe = async (ticketId: number) => {
        try {
            const response = await authenticatedFetch(`/api/tickets/atribuir.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ticket_id: ticketId })
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || 'Erro ao atribuir ticket');
            }

            // Recarregar tickets
            fetchTickets(currentFilters);
            
            toast({
                title: "Sucesso",
                description: result.message || "Ticket atribuído com sucesso!",
            });
        } catch (err) {
            console.error('Erro ao atribuir ticket:', err);
            toast({
                title: "Erro",
                description: err instanceof Error ? err.message : "Erro ao atribuir ticket",
                variant: "destructive",
            });
        }
    };

    // Callback quando atribuição é bem-sucedida
    const handleAssignSuccess = () => {
        fetchTickets(currentFilters);
        toast({
            title: "Sucesso",
            description: "Ticket atribuído com sucesso!",
        });
    };

    // Iniciar trabalho no ticket
    const handleStartWork = async (ticketId: number) => {
        try {
            const response = await authenticatedFetch(`/api/tickets/atribuir.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    ticket_id: ticketId,
                    action: 'start'
                })
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || 'Erro ao iniciar trabalho');
            }

            // Recarregar tickets
            fetchTickets(currentFilters);
            
            toast({
                title: "Sucesso",
                description: result.message || "Trabalho iniciado com sucesso!",
            });
        } catch (err) {
            console.error('Erro ao iniciar trabalho:', err);
            toast({
                title: "Erro",
                description: err instanceof Error ? err.message : "Erro ao iniciar trabalho",
                variant: "destructive",
            });
        }
    };

    // Handle ação completa
    const handleActionComplete = () => {
        // Recarregar com filtros atuais
        fetchTickets(currentFilters);
    };

    return (
        <div className="min-h-screen bg-gray-50 py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="space-y-6">
                    {/* Header */}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => navigate(-1)}
                                className="text-gray-700 hover:text-gray-900"
                                title="Voltar"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </Button>
                            <div>
                                <h1 className="text-3xl font-bold flex items-center gap-2 text-gray-900">
                                    <Ticket className="w-8 h-8" />
                                    Administração de Tickets
                                </h1>
                                <p className="text-gray-600 mt-1">
                                    Gerenciamento de solicitações de suporte
                                </p>
                            </div>
                        </div>
                        <Button
                            variant="outline"
                            size="icon"
                            onClick={() => fetchTickets(currentFilters)}
                            disabled={isLoading}
                            className="border-gray-300"
                        >
                            <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
                        </Button>
                    </div>

                    {/* Filtros */}
                    <TicketFilterBar onFilter={handleFilter} isLoading={isLoading} />

                    {/* Info de Resultados */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-sm text-blue-900 font-medium">
                            <span className="font-bold">{pagination.total}</span> ticket
                            {pagination.total !== 1 ? "s" : ""} encontrado
                            {pagination.total !== 1 ? "s" : ""} • Página{" "}
                            <span className="font-bold">{pagination.page}</span> de{" "}
                            <span className="font-bold">{pagination.total_pages || 1}</span>
                        </p>
                    </div>

            {/* Erro */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex gap-2" data-testid="error-message">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="font-semibold text-red-800">Erro</p>
                        <p className="text-sm text-red-700">{error}</p>
                    </div>
                </div>
            )}

            {/* Lista de Tickets */}
            <TicketListAdmin
                tickets={tickets}
                isLoading={isLoading}
                currentUserId={currentUser?.id}
                onAction={handleAction}
                onAssign={() => { }} // TODO: Implementar
            />

            {/* Paginação */}
            {pagination.total_pages > 1 && (
                <div className="flex justify-center gap-2" data-testid="pagination">
                    <Button
                        data-testid="prev-page-btn"
                        variant="outline"
                        disabled={pagination.page <= 1 || isLoading}
                        onClick={() => {
                            const newPage = pagination.page - 1;
                            setCurrentFilters(prev => ({ ...prev, page: newPage }));
                            fetchTickets({ ...currentFilters, page: newPage });
                        }}
                        className="border-gray-300"
                    >
                        Anterior
                    </Button>

                    {Array.from({ length: Math.min(5, pagination.total_pages) }).map(
                        (_, idx) => {
                            const pageNum = idx + 1;
                            return (
                                <Button
                                    key={pageNum}
                                    data-testid="current-page"
                                    variant={pageNum === pagination.page ? "default" : "outline"}
                                    onClick={() => {
                                        setCurrentFilters(prev => ({ ...prev, page: pageNum }));
                                        fetchTickets({ ...currentFilters, page: pageNum });
                                    }}
                                    disabled={isLoading}
                                    className={pageNum === pagination.page ? "" : "border-gray-300"}
                                >
                                    {pageNum}
                                </Button>
                            );
                        }
                    )}

                    <Button
                        data-testid="next-page-btn"
                        variant="outline"
                        disabled={pagination.page >= pagination.total_pages || isLoading}
                        onClick={() => {
                            const newPage = pagination.page + 1;
                            setCurrentFilters(prev => ({ ...prev, page: newPage }));
                            fetchTickets({ ...currentFilters, page: newPage });
                        }}
                        className="border-gray-300"
                    >
                        Próximo
                    </Button>
                </div>
            )}

            {/* Modal de Ações */}
            {selectedTicketId && (
                <TicketActionModal
                    isOpen={modalOpen}
                    onClose={() => {
                        setModalOpen(false);
                        setSelectedTicketId(null);
                    }}
                    ticketId={selectedTicketId}
                    ticketNumber={selectedTicketNumber}
                    currentStatus={selectedTicketStatus}
                    onActionComplete={handleActionComplete}
                />
            )}

            {/* Modal de Atribuição */}
            {assignTicketId && assignTicketData && (
                <AssignTicketModal
                    isOpen={assignModalOpen}
                    onClose={() => {
                        setAssignModalOpen(false);
                        setAssignTicketId(null);
                        setAssignTicketData(null);
                    }}
                    ticket={{
                        id: assignTicketId,
                        ticket_number: assignTicketNumber,
                        assigned_to: assignTicketData.assigned_to,
                        assigned_to_name: assignTicketData.assigned_to_name
                    }}
                    mode={assignMode}
                    onSuccess={handleAssignSuccess}
                />
            )}
                </div>
            </div>
        </div>
    );
};

export default AdminTicketsPage;
